import { useAuth } from '../../hooks/useAuth';
import { Link, useLocation } from 'react-router-dom';
import {
  Home,
  Users,
  X,
  Database,
  Award,
  User,
  BookOpen,
  GraduationCap,
  FileText,
  School,
  MessageSquare,
  Bookmark,
  Settings2,
  UserCheck,
} from 'lucide-react';

type SidebarProps = {
  isOpen: boolean;
  toggleSidebar: () => void;
};

type NavItem = {
  name: string;
  icon: JSX.Element;
  path: string;
  exact?: boolean;
};

type NavSection = {
  title?: string;
  items: NavItem[];
};

const Sidebar = ({ isOpen, toggleSidebar }: SidebarProps) => {
  const { userRole } = useAuth();
  const location = useLocation();

  const isActive = (path: string, exact = false) => {
    if (exact) return location.pathname === path;
    // For nested paths, match prefix but require a trailing /
    if (path === '/dashboard' || path === '/admin' || path === '/trainer' || path === '/student') {
      return location.pathname === path;
    }
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const getSections = (): NavSection[] => {
    switch (userRole) {
      case 'admin':
        return [
          {
            items: [
              {
                name: 'Tableau de bord',
                icon: <Home className="w-5 h-5" />,
                path: '/admin',
                exact: true,
              },
            ],
          },
          {
            title: 'Pédagogie',
            items: [
              { name: 'Classes', icon: <Bookmark className="w-5 h-5" />, path: '/admin/classes' },
              { name: 'Matières', icon: <BookOpen className="w-5 h-5" />, path: '/admin/subjects' },
              { name: 'Étudiants', icon: <User className="w-5 h-5" />, path: '/admin/students' },
              { name: 'Notes', icon: <Award className="w-5 h-5" />, path: '/admin/grades' },
              {
                name: 'Appréciations',
                icon: <MessageSquare className="w-5 h-5" />,
                path: '/admin/appreciations',
              },
              {
                name: 'Conseil de classe',
                icon: <UserCheck className="w-5 h-5" />,
                path: '/admin/conseil',
              },
              { name: 'Livrets scolaires', icon: <FileText className="w-5 h-5" />, path: '/admin/livrets' },
            ],
          },
          {
            title: 'Configuration',
            items: [
              { name: 'Établissement', icon: <School className="w-5 h-5" />, path: '/admin/etablissement' },
              { name: 'Utilisateurs', icon: <Users className="w-5 h-5" />, path: '/admin/users' },
            ],
          },
        ];
      case 'formateur':
        return [
          {
            items: [
              { name: 'Tableau de bord', icon: <Home className="w-5 h-5" />, path: '/trainer', exact: true },
              { name: 'Mes étudiants', icon: <UserCheck className="w-5 h-5" />, path: '/trainer/students' },
              { name: 'Notes', icon: <Award className="w-5 h-5" />, path: '/trainer/grades' },
            ],
          },
        ];
      case 'etudiant':
        return [
          {
            items: [
              { name: 'Tableau de bord', icon: <Home className="w-5 h-5" />, path: '/student', exact: true },
              { name: 'Mes notes', icon: <GraduationCap className="w-5 h-5" />, path: '/student/grades' },
              { name: 'Mon livret', icon: <FileText className="w-5 h-5" />, path: '/student/livret' },
              { name: 'Mon profil', icon: <User className="w-5 h-5" />, path: '/student/profile' },
            ],
          },
        ];
      default:
        return [
          {
            items: [{ name: 'Tableau de bord', icon: <Home className="w-5 h-5" />, path: '/dashboard' }],
          },
        ];
    }
  };

  const sections = getSections();

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={toggleSidebar}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 bg-white border-r border-gray-200 w-64 z-50 transform transition-transform duration-200 ease-in-out md:translate-x-0 md:sticky md:top-0 md:h-screen md:z-10 ${
          isOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="h-16 flex items-center justify-between px-5 border-b border-gray-200 shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-primary-600 text-white flex items-center justify-center font-bold">
                F
              </div>
              <span className="text-base font-semibold text-gray-900">FormaNote</span>
            </div>
            <button
              onClick={toggleSidebar}
              className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100 md:hidden"
              aria-label="Fermer le menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 py-4 overflow-y-auto">
            {sections.map((section, idx) => (
              <div key={idx} className={idx > 0 ? 'mt-6' : ''}>
                {section.title && (
                  <h3 className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    {section.title}
                  </h3>
                )}
                <ul className="space-y-0.5">
                  {section.items.map((item) => {
                    const active = isActive(item.path, item.exact);
                    return (
                      <li key={item.path}>
                        <Link
                          to={item.path}
                          onClick={() => {
                            if (window.innerWidth < 768) toggleSidebar();
                          }}
                          className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                            active
                              ? 'bg-primary-50 text-primary-700'
                              : 'text-gray-700 hover:bg-gray-100'
                          }`}
                        >
                          <span className={active ? 'text-primary-600' : 'text-gray-400'}>
                            {item.icon}
                          </span>
                          {item.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>

          {/* Footer */}
          <div className="p-3 border-t border-gray-200 shrink-0">
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-gray-500">
              <Database className="w-3.5 h-3.5" />
              <span className="capitalize">{userRole || 'Utilisateur'}</span>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
